<template>
  <b-card
    title="AI Configurations"
    tag="article"
    class="mb-2 content-wrapper"
  >
    <!-- View Mode -->
    <transition
      name="fade-slide"
      mode="out-in"
    >

      <div
        v-if="isLoading || isUpdatingCustomAI"
        key="loader"
        class="text-center py-5"
      >
        <b-spinner
          variant="primary"
          label="Loading..."
        />
      </div>
      <div
        v-else
        key="content"
      >
        <b-form-group>
          <div class="d-flex align-items-center">
            <b-form-radio-group
              v-model="customAiModel"
              name="ai-config-radios"
              :disabled="isUpdatingCustomAI"
            >
              <b-form-radio :value="0">
                Default
              </b-form-radio>
              <b-form-radio :value="1">
                Custom AI Configurations
              </b-form-radio>
            </b-form-radio-group>
          </div>
        </b-form-group>

        <div
          v-if="institution && institution.custom_ai == 0"
          class="mt-2 pl-1 mb-3"
        >
          <b-row class="align-items-center">
            <b-col md="6">
              <b-form-group
                label="Default AI Provider"
                label-for="default-ai-provider"
              >
                <v-select
                  id="default-ai-provider"
                  v-model="defaultProviderValue"
                  :options="aiConfigurationsTypesLookups"
                  :disabled="isUpdatingCustomAI || aiConfigurationsTypesLookups.length === 0"
                  autocomplete="off"
                  placeholder="Select Default AI Provider"
                />
                <small class="form-hint">Choose which default provider's configurations (.env) to use</small>
              </b-form-group>
            </b-col>
            <b-col
              md="6"
              class="mb-1 d-flex align-items-center"
            >
              <b-button
                variant="primary"
                class="submit-btn rounded-pill py-1 px-2"
                :disabled="isUpdatingCustomAI || !defaultProviderValue"
                @click="saveDefaultAIProvider(defaultProviderValue)"
              >
                <span v-if="!isUpdatingCustomAI">Save Default Provider</span>
                <b-spinner
                  v-else
                  small
                />
              </b-button>
            </b-col>
          </b-row>
        </div>

        <div v-if="institution && institution.custom_ai == 1">
          <div
            v-if="aiConfigMode === 'view' && aiConfigurations"
            key="view"
          >
            <div class="d-flex justify-content-end mb-3">
              <b-button
                variant="outline-primary"
                size="sm"
                class="btn btn-primary rounded d-flex align-items-center gap-2 py-75 px-1 rounded-pill"
                @click="aiConfigMode = 'edit'"
              >
                Edit Configurations
              </b-button>
            </div>

            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">AI Provider</span>
                <span class="info-value">
                  {{
                    aiConfigurationsTypesLookups.find(t => t.id === aiConfigurations.type)
                      ? aiConfigurationsTypesLookups.find(t => t.id === aiConfigurations.type).label
                      : aiConfigurations.type
                  }}
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">Deployment Name</span>
                <span class="info-value">{{ aiConfigurations.deploymentName }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">API Version</span>
                <span class="info-value">{{ aiConfigurations.apiVersion }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Base URL</span>
                <span class="info-value">{{ aiConfigurations.baseUrl }}</span>
              </div>
              <div
                class="info-item"
                style="grid-column: 1 / -1;"
              >
                <span class="info-label">API Key</span>
                <!-- Masking API Key for security visual -->
                <span class="info-value masked">
                  {{ aiConfigurations.apiKey }}
                </span>
              </div>
            </div>
          </div>
          <!-- Edit Mode -->
          <div
            v-else
            key="edit"
          >
            <validation-observer
              ref="aiConfigObserver"
              #default="{ invalid }"
            >
              <b-form
                class="ai-config-form"
                autocomplete="off"
                @submit.prevent="submitAIForm"
              >
                <b-row>
                  <b-col md="6">
                    <b-form-group
                      label="AI Provider"
                      label-for="type"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Type"
                        rules="required"
                      >
                        <v-select
                          id="type"
                          v-model="InstitutionForm.type"
                          name="type"
                          :options="aiConfigurationsTypesLookups"
                          :disabled="aiConfigurationsTypesLookups.length === 0"
                          autocomplete="off"
                          :reduce="(type) => type"
                          label="label"
                        />
                        <small class="form-hint">Select AI provider</small>
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label=" deployment Name"
                      label-for="deployment Name"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="deployment Name"
                        rules="required"
                      >
                        <b-form-input
                          id="deployment Name"
                          v-model="InstitutionForm.deploymentName"
                          name="deployment Name"
                          placeholder="deployment Name"
                        />
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="API Version"
                      label-for="apiVersion"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="API Version"
                        rules="required"
                      >
                        <b-form-input
                          id="apiVersion"
                          v-model="InstitutionForm.apiVersion"
                          name="apiVersion"
                          placeholder="e.g., 2023-12-01-preview"
                        />
                        <small class="form-hint">The version of the API to use</small>
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="Base URL"
                      label-for="baseUrl"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Base URL"
                        rules="required"
                      >
                        <b-form-input
                          id="baseUrl"
                          v-model="InstitutionForm.baseUrl"
                          name="baseUrl"
                          placeholder="https://api.example.com"
                        />
                        <small class="form-hint">Base URL for AI service endpoints</small>
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="API Key"
                      label-for="apiKey"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="API Key"
                        rules="required"
                      >
                        <b-form-input
                          id="apiKey"
                          v-model="InstitutionForm.apiKey"
                          name="apiKey"
                          placeholder="Enter your API key"
                          type="text"
                        />
                        <small class="form-hint">Your secure API key for AI services</small>
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
                <div class="form-actions d-flex justify-content-end gap-2">
                  <b-button
                    v-if="aiConfigurations && aiConfigurations.id"
                    variant="secondary"
                    class="mr-1 rounded-pill"
                    :disabled="isSubmitting"
                    @click="cancelEditAI"
                  >
                    Cancel
                  </b-button>
                  <b-button
                    type="submit"
                    variant="primary"
                    class="submit-btn rounded-pill"
                    :disabled="isSubmitting"
                  >
                    <span v-if="!isSubmitting">Save AI Configuration</span>
                    <b-spinner
                      v-else
                      small
                    />
                  </b-button>
                </div>
              </b-form>
            </validation-observer>
          </div>
        </div>
      </div>

    </transition>
  </b-card>
</template>

<script>
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BSpinner,
  BForm,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormRadioGroup,
} from 'bootstrap-vue'

export default {
  name: 'InstitutionAiConfiguration',
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BSpinner,
    BForm,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BFormRadioGroup,
    vSelect,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    institutionId: {
      required: true,
    },
    institution: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters({
      aiConfigurations: 'institutions/aiConfigurations',
      aiConfigurationsTypesLookups: 'institutions/aiConfigurationsTypesLookups',
    }),
    customAiModel: {
      get() {
        return this.institution ? this.institution.custom_ai : 0
      },
      set(value) {
        this.updateCustomAIConfig(value)
      },
    },
  },
  watch: {
    institution: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.defaultProviderValue = newVal.default_ai_provider
        }
      },
    },
  },
  data() {
    return {
      formMode: 'add',
      isSubmitting: false,
      InstitutionForm: {
        deploymentName: '',
        apiKey: '',
        apiVersion: '',
        baseUrl: '',
        type: null,
      },
      defaultProviderValue: null,
      aiConfigMode: 'view',
      isUpdatingCustomAI: false,
      isLoading: false,
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.isLoading = true
      this.$store.dispatch('institutions/aiConfigurationsTypesLookups')
      this.$store.dispatch('institutions/aiConfigurations', this.institutionId).then(() => {
        if (this.aiConfigurations && this.aiConfigurations.id) {
          this.formMode = 'edit'
          this.aiConfigMode = 'view'
          this.populateAIForm()
        } else {
          this.formMode = 'add'
          this.aiConfigMode = 'edit'
          this.resetAIForm()
        }
      }).finally(() => {
        this.isLoading = false
      })
    },
    saveDefaultAIProvider(value) {
      this.isUpdatingCustomAI = true
      this.$store
        .dispatch('institutions/updateCustomAIConfig', {
          id: this.institutionId,
          query: { default_ai_provider: value },
        })
        .then(() => {
          this.$store.dispatch('institutions/get', this.institutionId).then(() => {
            this.isUpdatingCustomAI = false
            this.$swal({
              title: 'Success!',
              text: 'Default AI provider updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK',
            })
          })
        })
        .catch(error => {
          this.isUpdatingCustomAI = false
          let errorMsg = 'An error occurred.'
          if (error?.response?.data?.errors) {
            errorMsg = Object.entries(error.response.data.errors)
              .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
              .join('\n')
          } else if (error?.response?.data?.message) {
            errorMsg = error.response.data.message
          }
          this.$swal({
            title: 'Error!',
            text: errorMsg,
            icon: 'error',
            confirmButtonText: 'OK',
          })
        })
    },
    updateCustomAIConfig(value) {
      this.isUpdatingCustomAI = true
      this.$store.dispatch('institutions/updateCustomAIConfig', {
        id: this.institutionId,
        query: { custom_ai: value },
      }).then(() => {
        this.$store.dispatch('institutions/get', this.institutionId).then(() => {
          this.isUpdatingCustomAI = false
        })
      }).catch(error => {
        this.isUpdatingCustomAI = false
        let errorMsg = 'An error occurred.'
        if (error?.response?.data?.errors) {
          errorMsg = Object.entries(error.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n')
        } else if (error?.response?.data?.message) {
          errorMsg = error.response.data.message
        }
        this.$swal({
          title: 'Error!',
          text: errorMsg,
          icon: 'error',
          confirmButtonText: 'OK',
        })
      })
    },
    submitAIForm() {
      this.$refs.aiConfigObserver.validate().then(success => {
        if (success) {
          this.isSubmitting = true
          if (this.formMode === 'edit') {
            this.$store.dispatch('institutions/updateAiConfigurations', this.InstitutionForm).then(() => {
              this.isSubmitting = false
              this.$swal({
                title: 'Success!',
                text: 'AI configurations updated successfully.',
                icon: 'success',
                confirmButtonText: 'OK',
              })
              this.init()
              this.aiConfigMode = 'view'
            }).catch(error => {
              let errorMsg = 'An error occurred.'
              if (error?.response?.data?.errors) {
                errorMsg = Object.entries(error.response.data.errors)
                  .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
                  .join('\n')
              } else if (error?.response?.data?.message) {
                errorMsg = error.response.data.message
              }
              this.$swal({
                title: 'Error!',
                text: errorMsg,
                icon: 'error',
                confirmButtonText: 'OK',
              })
              this.isSubmitting = false
            })
          } else {
            this.InstitutionForm.institution_id = this.institutionId
            this.$store.dispatch('institutions/storeAiConfigurations', this.InstitutionForm).then(() => {
              this.isSubmitting = false
              this.$swal({
                title: 'Success!',
                text: 'AI configurations saved successfully.',
                icon: 'success',
                confirmButtonText: 'OK',
              })
              this.init()
            }).catch(error => {
              this.isSubmitting = false
              let errorMsg = 'An error occurred.'
              if (error?.response?.data?.errors) {
                errorMsg = Object.entries(error.response.data.errors)
                  .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
                  .join('\n')
              } else if (error?.response?.data?.message) {
                errorMsg = error.response.data.message
              }
              this.$swal({
                title: 'Error!',
                text: errorMsg,
                icon: 'error',
                confirmButtonText: 'OK',
              })
            })
          }
        } else {
          this.$swal({
            icon: 'error',
            title: 'Validation Error',
            text: 'Please fill in all required fields.',
          })
        }
      })
    },
    populateAIForm() {
      // Ensure we have correct defaults
      this.InstitutionForm = {
        deploymentName: this.aiConfigurations.deploymentName || '',
        apiKey: this.aiConfigurations.apiKey || '',
        apiVersion: this.aiConfigurations.apiVersion || '',
        baseUrl: this.aiConfigurations.baseUrl || '',
        institution_id: this.institutionId,
        id: this.aiConfigurations.id,
        type: this.aiConfigurations.type,
      }
    },
    cancelEditAI() {
      this.aiConfigMode = 'view'
      this.populateAIForm() // Reset changes
    },
    resetAIForm() {
      if (this.institution) {
        this.InstitutionForm = {
          apiKey: this.institution.apiKey || '',
          apiVersion: this.institution.apiVersion || '',
          baseUrl: this.institution.baseUrl || '',
          type: this.institution.type || '',
        }
      } else {
        this.InstitutionForm = {
          apiKey: '',
          apiVersion: '',
          baseUrl: '',
          type: null,
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
  .content-wrapper {
    background: #000A1E;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    backdrop-filter: blur(10px);
    color: #e9ecef;
    animation: slideInLeft 0.5s ease forwards;

    @keyframes slideInLeft {
      from {
        opacity: 0;
        transform: translateX(-30px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(480px, 1fr));
    gap: 1.5rem;
    margin-top: 1rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .info-item {
    background: rgba(255, 255, 255, 0.05);
    padding: 1rem;
    border-radius: 10px;
    border-left: 4px solid rgba(15, 217, 222, 0.5);
    transition: transform 0.3s ease, border-color 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      border-left-color: rgba(133, 13, 204, 1);
      background: rgba(255, 255, 255, 0.08);
    }
  }

  .info-label {
    display: block;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .info-value {
    display: block;
    font-size: 1.1rem;
    color: #fff;
    font-weight: 500;
    word-break: break-word;
  }

  .masked {
    font-family: 'Courier New', monospace;
    background: rgba(0, 0, 0, 0.2);
    padding: 2px 8px;
    border-radius: 4px;
    display: inline-block;
  }

  .ai-config-form {
    .modern-input {
      background: rgba(255, 255, 255, 0.05) !important;
      border: 1px solid rgba(255, 255, 255, 0.2) !important;
      color: #fff !important;
      border-radius: 8px;
      padding: 0.75rem 1rem;
      transition: all 0.3s ease;

      &:focus {
        background: rgba(255, 255, 255, 0.08) !important;
        border-color: rgba(15, 217, 222, 0.5) !important;
        box-shadow: 0 0 0 3px rgba(15, 217, 222, 0.1) !important;
      }

      &::placeholder {
        color: rgba(255, 255, 255, 0.4) !important;
      }
    }

    .form-hint {
      display: block;
      margin-top: 0.5rem;
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.85rem;
    }
  }

  .form-actions {
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  .submit-btn {
    background: linear-gradient(90deg, rgba(133, 13, 204, 1) 0%, rgba(15, 217, 222, 1) 100%) !important;
    border: none;
    padding: 0.75rem 2rem;
    border-radius: 8px;
    font-weight: 600;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(133, 13, 204, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
</style>
